import type {
  LockerverseCheckoutEstimate,
  LockerversePreparedCheckout,
} from "@lockerverse/sdk/checkout";
import { formatMoney } from "./format-money.ts";

export function PaymentSummary({
  quote,
  displayedTipCents = quote.tipCents,
  displayedTotalCents = quote.totalCents,
  displayedBaseCents = displayedTotalCents - displayedTipCents,
}: {
  displayedBaseCents?: number;
  displayedTipCents?: number;
  displayedTotalCents?: number;
  quote: LockerverseCheckoutEstimate | LockerversePreparedCheckout;
}) {
  let cadence: { label: string } | null = null;
  if (quote.paymentOption === "monthly") {
    cadence = { label: "Monthly" };
  } else if (quote.paymentOption === "annually") {
    cadence = { label: "Annual" };
  }
  const displayedServiceFeeCents = Math.max(
    0,
    displayedBaseCents - quote.subtotalCents + quote.discountCents
  );
  return (
    <section
      aria-label="Payment summary"
      className="lockerverse-payment__payment-summary"
    >
      <h2>Order summary</h2>
      <div className="lockerverse-payment__payment-summary-items">
        {quote.lineItems.map((item) => (
          <div
            className="lockerverse-payment__payment-summary-row"
            key={item.listingId}
          >
            <span>
              {item.productName}
              {item.quantity > 1 ? (
                <>
                  {" "}
                  <small>× {item.quantity}</small>
                </>
              ) : null}
              {item.paymentOption === "monthly" ? (
                <small> · Monthly</small>
              ) : null}
              {item.paymentOption === "annually" ? (
                <small> · Annual</small>
              ) : null}
            </span>
            <strong>{formatMoney(item.subtotalCents)}</strong>
          </div>
        ))}
      </div>
      <div className="lockerverse-payment__payment-summary-totals">
        {displayedServiceFeeCents > 0 ? (
          <div className="lockerverse-payment__payment-summary-row lockerverse-payment__payment-summary-detail">
            <span>Service fee</span>
            <strong>{formatMoney(displayedServiceFeeCents)}</strong>
          </div>
        ) : null}
        {quote.discountCents > 0 && quote.appliedDiscount ? (
          <div className="lockerverse-payment__payment-summary-row lockerverse-payment__payment-summary-discount">
            <span>
              {quote.appliedDiscount.source === "email_list"
                ? "Email discount"
                : "Promo code discount"}{" "}
              ({quote.appliedDiscount.percentageOff}% off)
            </span>
            <strong>-{formatMoney(quote.discountCents)}</strong>
          </div>
        ) : null}
        {displayedTipCents > 0 ? (
          <div className="lockerverse-payment__payment-summary-row lockerverse-payment__payment-summary-detail lockerverse-payment__payment-summary-tip">
            <span>Tip</span>
            <strong>{formatMoney(displayedTipCents)}</strong>
          </div>
        ) : null}
        <div className="lockerverse-payment__payment-summary-row lockerverse-payment__payment-summary-total">
          <span>{cadence ? `${cadence.label} total` : "Total"}</span>
          <strong>{formatMoney(displayedTotalCents)}</strong>
        </div>
      </div>
    </section>
  );
}
